import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { MinimumWageAllowanceExpandableItemSection } from "../containers/MinimumWageAllowanceExpandableItemSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { GenericMinimumWageExpandableItemSection } from "../containers/GenericMinimumWageExpandableItemSection";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";
import { MinimumWageCountryAdditionExpandableItemSection } from "../containers/MinimumWageCountryAdditionExpandableItemSection";
import { FranceMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/franceMinimumWageConfigurationModel";

import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const qualificationTypes = [
  DriverQualificationsType.NotSet,
  DriverQualificationsType.VehiclesTo3Comma5,
  DriverQualificationsType.VehiclesFrom3Comma5To11,
  DriverQualificationsType.VehiclesFrom11To19,
  DriverQualificationsType.VehiclesAbove19,
  DriverQualificationsType.HighQualifiedTruckDriver,
  DriverQualificationsType.BusDriver,
  DriverQualificationsType.BusDriverAndConductor,
  DriverQualificationsType.TouristBusDriver,
  DriverQualificationsType.BigBusDriver,
]

const defaultFields: FieldName[] = [
    'increaseRateAccordingToWorkYears',
    'addExtraAditionForCountry',    
    'includeAdditionForNightTimeWork',
    'includeAdditionForHolidayWork',
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const allowedCurrenciesInner = [
    'EUR',
]

const allowedCurrencies = [
    'EUR',
    'PLN',
]

const translateKey = 'franceConfiguration'

const country = 'france'

export const FranceMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<FranceMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.franceConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('franceConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
        allowanceExpandableItems: t('configuration.minimumWages.minimalAllowanceTitle'),
        countryAdditionsExpandableItems: t('configuration.minimumWages.minimalCountryAdditionTitle'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.franceConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, franceConfiguration: state })}
            handleCancel={() => setState(q.data.franceConfiguration)}
            dataTestId="france-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.franceConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <GenericMinimumWageExpandableItemSection
                        allowedCurrencies={allowedCurrenciesInner}
                        qualificationTypes={qualificationTypes} 
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageAllowanceExpandableItemSection
                        allowedCurrencies={allowedCurrencies}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageCountryAdditionExpandableItemSection 
                        allowedCurrencies={allowedCurrencies}
                        title={t('configuration.minimumWages.additionForLongTripTitle')}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey={translateKey}
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection 
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
